import { Injectable } from '@angular/core';
import { HttpService } from 'projects/customer/src/app/shared/services/http-service.service';
import { LocalStorageService } from '../../../shared/services/local-storage.service';
import { Observable } from 'rxjs';
import { ApiResponse } from '../../../shared/models';

@Injectable({
    providedIn: 'root'
})
export class DashboardService {
    timeZone: any;
    timezoneOffset: string;
    constructor(
        private httpService: HttpService,
        private localStorageService: LocalStorageService
    ) {
        this.getTimezone();
    }

    getTimezone() {
        const date = new Date();
        const new_str = date.toString();
        const checkPlusMinus = new_str.slice(28, 29);
        const result = new_str.slice(29, 33);
        if (checkPlusMinus == '+') {
            const a = '%2B';
            const b = result;
            const c = '%3A';
            this.timeZone = [a, b.slice(0, 2), c, b.slice(2)].join('');
        }
        if (checkPlusMinus == '-') {
            const a = '-';
            const b = result;
            const c = '%3A';
            this.timeZone = [a, b.slice(0, 2), c, b.slice(2)].join('');
        }

        this.timezoneOffset = `?timezone_offset=${this.timeZone}`;
    }

    private buildPeriod(period: any): string {
        if (!period) return '';
        return typeof period === 'string'
            ? `&filter[period]=${period}`
            : `&filter[start_date_time]=${period.startDate}&filter[end_date_time]=${period.endDate}`;
    }

    private buildWarehouseId(warehouse_id: string): string {
        return warehouse_id ? `&filter[warehouse_ids][0]=${warehouse_id}` : '';
    }

    getProductAvailabilityData(warehouse_id: string) {
        let warehouseQuery = this.buildWarehouseId(warehouse_id);

        if (warehouse_id) {
            warehouseQuery = warehouseQuery.replace('&', '?');
        }

        const path = `dashboard/product_availability${warehouseQuery}`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getDeliveryByCountries(period: string) {
        const periodQuery = this.buildPeriod(period);
        const path = `dashboard/delivery_by_countries${this.timezoneOffset}${periodQuery}`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getMarketPlaceData(period: string) {
        const periodQuery = this.buildPeriod(period);
        const path = `dashboard/marketplaces_orders${this.timezoneOffset}${periodQuery}`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getTotalParcel(period: string, activeShippingTypeId: string, activeRetailerId: string) {
        const periodQuery = this.buildPeriod(period);
        let path = `dashboard/parcels_by_retailers${this.timezoneOffset}${periodQuery}`;
        if (activeShippingTypeId && !activeShippingTypeId.includes('ALL SERVICES')) {
            path += `&filter[services]=${activeShippingTypeId}`;
        }
        if (activeRetailerId && !activeRetailerId.includes('RETAILER NAME')) {
            path += `&filter[retailer_ids]=${activeRetailerId}`;
        }
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getParcelByCountries(period: any) {
        const periodQuery = this.buildPeriod(period);
        const path = `dashboard/parcel_by_countries${this.timezoneOffset}${periodQuery}`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getParcelServices(period: any) {
        const periodQuery = this.buildPeriod(period);
        const path = `dashboard/control_tower/services${this.timezoneOffset}${periodQuery}`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getShippingTypes() {
        const path = 'orders/shipping_types';
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getInboundData(period: any, warehouse_id: string) {
        let periodQuery = this.buildPeriod(period);
        const warehouseQuery = this.buildWarehouseId(warehouse_id);
        
        periodQuery = periodQuery.replace('&', '?');
        
        const path = `dashboard/inbound${periodQuery}${warehouseQuery}`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }


    getOrderByStatusData(period: any, warehouse_id: string) {
        const periodQuery = this.buildPeriod(period);
        const warehouseQuery = this.buildWarehouseId(warehouse_id);
        const path = `dashboard/total_orders_by_status${this.timezoneOffset}${periodQuery}${warehouseQuery}`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getTotalOrdersData(period: any, warehouse_id: string) {
        const periodQuery = this.buildPeriod(period);
        const warehouseQuery = this.buildWarehouseId(warehouse_id);
        const path = `dashboard/total_orders${this.timezoneOffset}${periodQuery}${warehouseQuery}`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getTotalSipmentsData(period: any, activeShippingTypeId: number) {
        const periodQuery = this.buildPeriod(period);
        let path = `dashboard/total_shipments${this.timezoneOffset}${periodQuery}`;
        if (activeShippingTypeId > 0) {
            path += `&filter[shipping_type_id]=${activeShippingTypeId}`;
        }
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getInventoryCountData(period: any, warehouse_id: string) {
        const periodQuery = this.buildPeriod(period);
        const warehouseQuery = this.buildWarehouseId(warehouse_id);
        const path = `dashboard/inventory_count${this.timezoneOffset}${periodQuery}${warehouseQuery}`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getReturnOrdersData(period: any, warehouse_id: string) {
        const periodQuery = this.buildPeriod(period);
        const warehouseQuery = this.buildWarehouseId(warehouse_id);
        const path = `dashboard/total_return_orders${this.timezoneOffset}${periodQuery}${warehouseQuery}`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getShipmentByStatus(period: any, activeShippingTypeId: number) {
        const periodQuery = this.buildPeriod(period);
        let path = `dashboard/shipment_by_status${this.timezoneOffset}${periodQuery}`;
        if (activeShippingTypeId > 0) {
            path += `&shipping_type_id=${activeShippingTypeId}`;
        }
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }


    getRetailers(page, period?, searchPattern?: string) {
        let path = `dashboard/retailers?page[number]=${page}`;

        if (period) {
            const periodQuery = this.buildPeriod(period);
            path += `${periodQuery}`;
        }

        if (searchPattern)
            path = path + `&filter[search_pattern][0]=${searchPattern}`;

        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    isControlTower() {
        const data = this.localStorageService.get('userData');
        if ('channel_permissions' in (data || {})) {
            if (
                data?.channel_permissions?.findIndex((item) => {
                    return (
                        item?.channel_id == 8 && item?.customer_number != null
                    );
                }) != -1
            ) {
                return true;
            } else {
                return false;
            }
        } else {
            return false;
        }
    }

    getAllRetailers(page: number = 1, size: number = 10, id): Observable<any> {
        const path = `admin/v1/user_management/2/group_customer_mapping_list?filter[called_from]=customer_portal&page[number]=${page}&page[size]=${size}&filter[group_retailer_user_id]=${id}`;

        return this.httpService.callAuthGetApi(path, '', 'noAuth');
    }

    public getLoginAccess(param) {
        const path = 'frontend/v1/login/login_to_retailer';
        return this.httpService.callNoAuthPostApi(path, param);
    }
}
